import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useAuth } from "react-oidc-context";
import { useQuery } from "@tanstack/react-query";
import type { HospitalDto } from "@ambulance/shared";
import { ApiError, useApi } from "../api";
import { useMe } from "../hooks/useMe";
import { CheckCircle2, Loader2, LogIn, LogOut, User } from "lucide-react";
import { SearchableSelect } from "../components/SearchableSelect";

const todayLocal = (() => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
})();

// Shift options the user picks from (start, end). All 24 twelve-hour shifts —
// one starting on each hour (00:00–12:00, 01:00–13:00, … 23:00–11:00) — plus
// the non-12-hour shifts that were already in use.
const hhmm = (h: number) => `${String(h % 24).padStart(2, "0")}:00`;
const SHIFT_OPTIONS: [string, string][] = [
  ...Array.from(
    { length: 24 },
    (_, h): [string, string] => [hhmm(h), hhmm(h + 12)]
  ),
  ["09:00", "19:00"], // 10-hour
  ["15:00", "01:00"], // 10-hour
  ["08:00", "17:00"], // 9-hour
];

export function UnitLoginPage() {
  const auth = useAuth();
  const api = useApi();
  const { me } = useMe();
  const hospitalsQ = useQuery({ queryKey: ["hospitals"], queryFn: () => api.get<HospitalDto[]>("/api/v1/hospitals"), enabled: Boolean(api.token) });
  const hospitals = hospitalsQ.data?.map((h) => ({ ...h, _id: h.id }));
  const ambulances = useQuery({ queryKey: ["ambulances"], queryFn: () => api.get<{ id: string; number: string }[]>("/api/v1/ambulances"), enabled: Boolean(api.token) }).data;
  const unitCodes = useQuery({ queryKey: ["unit-codes"], queryFn: () => api.get<{ id: string; code: string }[]>("/api/v1/unit-codes"), enabled: Boolean(api.token) }).data;
  const login = (body: unknown) => api.post<{ id: string }>("/api/v1/units", body);

  const [f, setF] = useState({
    member1Name: "",
    member1IdNo: "",
    member2Name: "",
    member2IdNo: "",
    member3Name: "",
    member3IdNo: "",
    date: todayLocal,
    shiftStart: "",
    shiftEnd: "",
    hospitalId: "",
    ambulanceNo: "",
    ambulanceType: "ALS" as "ALS" | "BLS" | "CC",
    unitCode: "",
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) =>
    setF((p) => ({ ...p, [k]: v }));

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!f.shiftStart || !f.shiftEnd) return setError("Please select a shift");
    if (!f.hospitalId) return setError("Please select a location (hospital)");
    if (!f.ambulanceNo) return setError("Please select an ambulance number");
    if (!f.unitCode) return setError("Please select a unit code");
    setSubmitting(true);
    try {
      await login({
        member1Name: f.member1Name,
        member1IdNo: f.member1IdNo,
        member2Name: f.member2Name,
        member2IdNo: f.member2IdNo,
        member3Name: f.member3Name,
        member3IdNo: f.member3IdNo,
        date: f.date,
        shiftStart: f.shiftStart,
        shiftEnd: f.shiftEnd,
        hospitalId: f.hospitalId,
        ambulanceNo: f.ambulanceNo,
        ambulanceType: f.ambulanceType,
        unitCode: f.unitCode,
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not log in the unit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Unit logged in
          </h1>
          <p className="text-slate-600 mb-6">
            Unit <span className="font-semibold">{f.unitCode}</span> is now
            active until the end of the shift ({f.shiftEnd}).
          </p>
          <div className="flex gap-2 flex-col">
            <button
              onClick={() => {
                setDone(false);
                setF((p) => ({
                  ...p,
                  member1Name: "",
                  member1IdNo: "",
                  member2Name: "",
                  member2IdNo: "",
                  member3Name: "",
                  member3IdNo: "",
                  shiftStart: "",
                  shiftEnd: "",
                  ambulanceNo: "",
                  unitCode: "",
                }));
              }}
              className="rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white font-semibold py-2.5 px-4"
            >
              Log in another unit
            </button>
            <Link
              to="/"
              className="rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2.5 px-4"
            >
              Back to home
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const inputCls =
    "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]";
  const labelCls =
    "block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1";

  return (
    <main className="flex-1 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-3xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img
              src="/hmg-logo.png"
              alt="HMG"
              width={140}
              height={36}
              className="h-7 sm:h-9 w-auto flex-shrink-0"
            />
            <div className="hidden md:block border-l border-slate-300 pl-3">
              <div className="text-xs uppercase tracking-wider text-[var(--hmg-red)] font-semibold">
                EMS
              </div>
              <div className="text-sm font-semibold text-slate-900">
                Unit Login
              </div>
            </div>
          </div>
          {me && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to="/profile"
                title="My Profile"
                className="text-slate-600 hover:text-slate-900 p-2 rounded inline-flex items-center gap-1.5 text-sm"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Profile</span>
              </Link>
              <button
                onClick={() => void auth.signoutRedirect()}
                title="Sign out"
                className="text-slate-600 hover:text-red-600 p-2 rounded inline-flex items-center gap-1.5 text-sm"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <form
        onSubmit={handleSubmit}
        className="flex-1 max-w-3xl mx-auto w-full px-3 sm:px-4 py-4 sm:py-6 space-y-4"
      >
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 space-y-4">
          <h2 className="font-semibold text-slate-900">Crew</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>
                Crew Member 1 Name <span className="text-red-500">*</span>
              </label>
              <input
                required
                value={f.member1Name}
                onChange={(e) => set("member1Name", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>
                Member 1 ID No <span className="text-red-500">*</span>
              </label>
              <input
                required
                value={f.member1IdNo}
                onChange={(e) => set("member1IdNo", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Crew Member 2 Name</label>
              <input
                value={f.member2Name}
                onChange={(e) => set("member2Name", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Member 2 ID No</label>
              <input
                value={f.member2IdNo}
                onChange={(e) => set("member2IdNo", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Crew Member 3 Name</label>
              <input
                value={f.member3Name}
                onChange={(e) => set("member3Name", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Member 3 ID No</label>
              <input
                value={f.member3IdNo}
                onChange={(e) => set("member3IdNo", e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 space-y-4">
          <h2 className="font-semibold text-slate-900">Shift & Unit</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={f.date}
                onChange={(e) => set("date", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>
                Shift <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={
                  f.shiftStart && f.shiftEnd
                    ? `${f.shiftStart}-${f.shiftEnd}`
                    : ""
                }
                onChange={(e) => {
                  const [s, en] = e.target.value.split("-");
                  setF((p) => ({
                    ...p,
                    shiftStart: s ?? "",
                    shiftEnd: en ?? "",
                  }));
                }}
                className={inputCls}
              >
                <option value="">Select shift…</option>
                {SHIFT_OPTIONS.map(([s, en]) => (
                  <option key={`${s}-${en}`} value={`${s}-${en}`}>
                    {s} - {en}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>
                Unit Allocation <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={f.hospitalId}
                onChange={(v) => set("hospitalId", v)}
                options={(hospitals ?? []).map((h) => ({
                  value: h._id,
                  label: h.name,
                }))}
                placeholder="Select location…"
              />
            </div>
            <div>
              <label className={labelCls}>
                Ambulance No <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={f.ambulanceNo}
                onChange={(v) => set("ambulanceNo", v)}
                options={(ambulances ?? []).map((a) => ({
                  value: a.number,
                  label: a.number,
                }))}
                placeholder="Select ambulance…"
              />
            </div>
            <div>
              <label className={labelCls}>
                Ambulance Type <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                {(["ALS", "BLS", "CC"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => set("ambulanceType", t)}
                    className={`flex-1 py-2 rounded-lg border text-sm font-semibold ${
                      f.ambulanceType === t
                        ? "bg-[var(--hmg-red)] text-white border-[var(--hmg-red)]"
                        : "bg-white border-slate-300 text-slate-700"
                    }`}
                    title={t === "CC" ? "Critical Care" : t}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>
                Unit team code <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={f.unitCode}
                onChange={(v) => set("unitCode", v)}
                options={(unitCodes ?? []).map((c) => ({
                  value: c.code,
                  label: c.code,
                }))}
                placeholder="Select unit code…"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-3 text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white font-semibold px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogIn className="w-4 h-4" />
            )}
            Log in unit
          </button>
        </div>
      </form>
    </main>
  );
}
